import { useEffect, useState, type ReactNode } from 'react'
import { fetchSession, fetchSignInTheme } from '../api'
import { applyTheme } from '../themes'
import type { User } from '../types'
import owLogo from '../assets/White-300x65.webp'

// What VMS's callback says went wrong, in the dispatcher's words.
const failures: Record<string, [title: string, text: string]> = {
  unverified: ['Google hasn’t verified this email', 'Overwatch only accepts a Google account whose email address Google has verified. Verify it with Google, or sign in with another account.'],
  cancelled: ['Sign-in was cancelled', 'Google didn’t finish signing you in, so nothing changed. Try again when you’re ready.'],
  expired: ['That sign-in took too long', 'The sign-in started more than 30 minutes ago, or in another tab. Start it again from here.'],
  failed: ['Sign-in didn’t work', 'Something went wrong between Overwatch and Google. Try again in a moment.'],
}

// Checked this often while waiting, so an approved account opens the
// dashboard without anyone thinking to reload.
const approvalCheckMs = 30_000

const goToDashboard = () => window.location.assign('/')
const goToSignIn = () => window.location.assign('/auth/login')

interface Props {
  path: string
  search: string
  /** Where an approved account goes; the dashboard. */
  onApproved?: () => void
  /** Where waiting goes once there is no session to wait with: sign-in. */
  onSignedOut?: () => void
}

// SignInPage is sign-in, waiting for approval and a failed sign-in, drawn by
// the web app so they follow the instance's theme (#286). VMS serves it at
// /auth/login and /auth/pending, and says what happened in the query.
export function SignInPage({ path, search, onApproved = goToDashboard, onSignedOut = goToSignIn }: Props) {
  useEffect(() => {
    void fetchSignInTheme().then((theme) => { if (theme) applyTheme(theme) })
  }, [])

  const query = new URLSearchParams(search)
  const reason = query.get('error') ?? ''
  // Own keys only: a plain object would answer ?error=constructor with a built-in.
  const failure = Object.hasOwn(failures, reason) ? failures[reason] : undefined
  const returnTo = query.get('return_to')
  const google = returnTo ? `/auth/google?return_to=${encodeURIComponent(returnTo)}` : '/auth/google'

  return (
    <div className="relative min-h-screen px-4 py-6 flex flex-col items-center justify-center gap-6 overflow-hidden bg-zinc-950 text-zinc-100">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-25 [background-size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,#000_0,transparent_70%)]"
        style={{ backgroundImage: 'linear-gradient(var(--color-zinc-800) 1px, transparent 1px), linear-gradient(90deg, var(--color-zinc-800) 1px, transparent 1px)' }}
      />
      <main className="relative w-full max-w-[400px] px-7 py-8 flex flex-col gap-5 rounded-lg border border-zinc-800 bg-zinc-900 shadow-[0_24px_64px_rgb(0_0_0/0.5)]">
        <img src={owLogo} alt="Overwatch" width={148} height={32} className="h-8 w-auto self-start" />
        {path.startsWith('/auth/pending') ? (
          <Waiting onApproved={onApproved} onSignedOut={onSignedOut} />
        ) : failure ? (
          <>
            <div role="alert" className="flex flex-col gap-1.5">
              <Badge tone="fault">Not signed in</Badge>
              <h1 className="mt-1.5 text-2xl font-bold tracking-tight">{failure[0]}</h1>
              <p className="text-zinc-400 leading-normal">{failure[1]}</p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <a href={google} className="flex-1 min-w-40 h-11 flex items-center justify-center rounded-md bg-orange-400 text-zinc-950 font-bold hover:bg-orange-300">Try again</a>
              <a href={google} className="h-11 px-4 flex items-center justify-center rounded-md border border-zinc-800 font-semibold hover:bg-zinc-800">Use another account</a>
            </div>
          </>
        ) : (
          <>
            {query.has('signed_out') && (
              <p role="status" className="px-3 py-2.5 flex items-center gap-2.5 rounded-md border border-zinc-800 bg-zinc-800/60 text-sm">
                <svg aria-hidden="true" className="w-4 h-4 text-live" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                You’re signed out.
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <h1 className="text-2xl font-bold tracking-tight">Sign in</h1>
              <p className="text-zinc-400 leading-normal">Watch your sites live and handle their alerts.</p>
            </div>
            {/* Google's own button, as their branding requires: white, its
                mark, and these words. */}
            <a href={google} className="h-12 flex items-center justify-center gap-3 rounded-md border border-[#DADCE0] bg-white text-[#1F1F1F] text-[15px] font-medium hover:bg-[#F8F9FA]">
              <GoogleMark />
              Sign in with Google
            </a>
            <p className="text-[13px] text-zinc-400 leading-normal">
              Use the Google account your administrator invited. A new account waits for their approval before it sees any site.
            </p>
          </>
        )}
      </main>
      <p className="relative text-xs font-mono text-zinc-400">Overwatch VMS</p>
    </div>
  )
}

function Waiting({ onApproved, onSignedOut }: { onApproved: () => void; onSignedOut: () => void }) {
  const [user, setUser] = useState<User | null>(null)
  const [unreachable, setUnreachable] = useState(false)
  useEffect(() => {
    let stopped = false
    const check = () => fetchSession().then((session) => {
      if (stopped) return
      // Only a session that is gone sends anyone to sign-in: a server error
      // did, though the session still worked (#287).
      if (session.state === 'signed-out') return onSignedOut()
      setUnreachable(session.state === 'unreachable')
      if (session.state !== 'signed-in') return
      setUser(session.user)
      if (session.user.active) onApproved()
    })
    void check()
    const timer = setInterval(() => void check(), approvalCheckMs)
    return () => { stopped = true; clearInterval(timer) }
  }, [onApproved, onSignedOut])

  const initials = (user?.name || user?.email || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Badge tone="attention">Waiting for approval</Badge>
        <h1 className="mt-1.5 text-2xl font-bold tracking-tight">An administrator has to let you in</h1>
        <p className="text-zinc-400 leading-normal">
          Your account is set up, but it can’t see any site until an administrator gives it access. This page checks again every 30 seconds and opens the dashboard once they have.
        </p>
      </div>
      {/* Always mounted, so a status that appears is announced. */}
      <p role="status" className={unreachable ? 'text-[13px] text-attention' : 'sr-only'}>
        {unreachable ? 'Can’t reach Overwatch right now. Still checking.' : ''}
      </p>
      {user && (
        <div className="p-3 flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-800/60">
          <span aria-hidden="true" className="w-9 h-9 shrink-0 rounded-full bg-orange-500/15 text-orange-300 flex items-center justify-center text-[13px] font-bold">{initials}</span>
          <span className="min-w-0 flex flex-col">
            {user.name && <span className="font-semibold">{user.name}</span>}
            <span className="text-[13px] text-zinc-400 [overflow-wrap:anywhere]">{user.email}</span>
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-2.5">
        {/* Google asks which account on every sign-in, so starting again is
            how another one is chosen. */}
        <a href="/auth/google" className="flex-1 min-w-40 h-11 flex items-center justify-center rounded-md border border-zinc-800 bg-zinc-800 font-semibold hover:bg-zinc-700">Use another account</a>
        <a href="/auth/logout" className="h-11 px-4 flex items-center justify-center rounded-md text-zinc-400 font-semibold hover:text-zinc-100">Sign out</a>
      </div>
    </>
  )
}

function Badge({ tone, children }: { tone: 'fault' | 'attention'; children: ReactNode }) {
  const look = tone === 'fault' ? 'border-fault/40 bg-fault/10 text-fault' : 'border-attention/55 bg-attention/10 text-attention'
  return (
    <span className={`self-start h-[26px] px-2.5 inline-flex items-center gap-1.5 rounded-full border text-[13px] font-semibold ${look}`}>
      <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d={tone === 'fault' ? 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 8v4M12 16h.01' : 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2'} />
      </svg>
      {children}
    </span>
  )
}

function GoogleMark() {
  return (
    <svg aria-hidden="true" className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}
